import { useEffect, useState } from 'react'
import { scrollState } from './scrollState'

function smoothstep(x, a, b) {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

export default function Overlays({ isMobile }) {
  // Force re-renders so Beat 5 opacity tracks scroll
  const [, force] = useState(0)
  useEffect(() => {
    let raf
    const loop = () => {
      force((n) => (n + 1) & 0xff)
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [])

  // Beat 5 scrolls out naturally with the page; we only fade its scrim if the user
  // somehow over-scrolls. EndCard only appears AFTER Beat 5 has scrolled past.
  const t = scrollState.offset
  const beat5Opacity = 1 // intentionally kept at 1 — let the page scroll handle exit

  return (
    <>
      {/* PILLAR 1 — Stakes — LEFT */}
      <Beat
        isMobile={isMobile}
        align="left"
        eyebrow="The stakes"
        h2={<>Delhi&rsquo;s air takes 11.9&nbsp;years off your&nbsp;life.</>}
        body="Coughing kids. Breathless parents. Lungs that never reach full size. The toll of breathing polluted air is measurable, cumulative, and quiet — and most of it happens indoors."
        href="https://aqi0.com/learn/delhi-life-expectancy-pollution/"
      />

      {/* PILLAR 2 — Year-round AND pan-Indian — RIGHT */}
      <Beat
        isMobile={isMobile}
        align="right"
        eyebrow="Year-round · Pan-Indian"
        h2={<>Not just winter.<br />Not just Delhi.</>}
        body="Air pollution stays above WHO limits 9–10 months a year. Even monsoon is ~3× WHO. From Lucknow to Patna, Kolkata to Mumbai — urban India keeps breathing harm year-round."
        href="https://aqi0.com/learn/delhi-ncr-aqi-year-round/"
      />

      {/* PILLAR 3 — Mass-balance physics — LEFT — "Indoor" is the key word */}
      <Beat
        isMobile={isMobile}
        align="left"
        eyebrow="Mass-balance physics"
        h2={<>Indoor purifiers<br />can't fix this polluted&nbsp;air.</>}
        body="Indian homes leak air 3× faster than American ones. Outdoor PM2.5 keeps coming back through every gap. The best indoor purifier reduces indoor PM2.5 by only 49%. And nothing removes CO₂."
        href="https://aqi0.com/learn/why-purifiers-cant-handle-delhi-winters/"
      />

      {/* CONNECTOR — full-bleed brand hero. The pivot from "purifiers can't" to "aqi0 can".
          Uses the installation photo backdrop set in Backgrounds.jsx (bg-connector.jpg). */}
      <ConnectorBeat />

      {/* PILLAR 4 — aqi0 promise. Strict L-R-L-R alternation continues here. */}
      <Beat
        isMobile={isMobile}
        align="right"
        eyebrow={<>The aqi0 promise · 1 of 3 &nbsp;·&nbsp; 🇯🇵 Japanese H13 HEPA</>}
        h2={<>Outdoor air enters<br />through&nbsp;H13&nbsp;HEPA.</>}
        body="Outdoor air is pulled in through three flat H13 HEPA panels in series — Japanese filter media trusted in cleanrooms and hospitals. Each panel captures what slipped past the last. Particles below 0.3 microns are caught before any of it reaches your home."
        href="https://aqi0.com/air-purifier/"
      />
      <Beat
        isMobile={isMobile}
        align="left"
        eyebrow={<>The aqi0 promise · 2 of 3 &nbsp;·&nbsp; 🇩🇪 German EC fan</>}
        h2={<>Your home<br />pressurises&nbsp;slightly.</>}
        body="Clean air is pushed indoors at positive pressure — about 700 m³/h, driven by a German-engineered EC fan. Outside air can no longer leak back in through the gaps. CO₂ is continuously flushed."
        href="https://aqi0.com/air-purifier/"
      />
      <Beat
        isMobile={isMobile}
        align="right"
        eyebrow="The aqi0 promise · 3 of 3"
        h2={<>Stale air exits<br />through natural&nbsp;gaps.</>}
        body="Positive pressure pushes used air — and the CO₂, cooking fumes, and stale particles — out through window seals and door gaps. One system. All rooms. All seasons."
        href="https://aqi0.com/air-purifier/"
      />

      {/* PILLAR 5 — Recovery. Fades out as EndCard centres in. */}
      <div style={{ opacity: beat5Opacity, transition: 'opacity 0.1s linear' }}>
        <Beat
          isMobile={isMobile}
          align="left"
          eyebrow="Recovery, not tolerance"
          h2={<>You don't adapt to&nbsp;pollution.<br />Clean air can heal&nbsp;you.</>}
          body="Your parents aren't adjusted to Delhi's air — they've accumulated it. But clean indoor hours aren't weakness, they're recovery. Markers improve in 48–72 hours. Children develop fuller lungs in cleaner air."
          href="https://aqi0.com/learn/can-the-body-adapt-to-air-pollution/"
        />
      </div>

      {/* Runway so Beat 5 can fully scroll out before the EndCard takes the centre */}
      <section style={{ minHeight: '150vh' }} />
    </>
  )
}

function ConnectorBeat() {
  return (
    <section
      style={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0 6vw',
        boxSizing: 'border-box',
        pointerEvents: 'none',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: '0.78rem',
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.75)',
          marginBottom: '1.1rem',
        }}
      >
        the only solution
      </div>
      <h2
        style={{
          margin: 0,
          fontSize: 'clamp(2.4rem, 7vw, 5.4rem)',
          fontWeight: 600,
          letterSpacing: '-0.04em',
          lineHeight: 0.98,
          color: '#fff',
          textShadow: '0 4px 28px rgba(0,0,0,0.65)',
          maxWidth: '24ch',
        }}
      >
        aqi0 air purifier.
      </h2>
      <h2
        style={{
          margin: '0.4rem 0 0',
          fontSize: 'clamp(2.4rem, 7vw, 5.4rem)',
          fontWeight: 600,
          letterSpacing: '-0.04em',
          lineHeight: 0.98,
          color: '#8AB63F',
          textShadow: '0 4px 28px rgba(0,0,0,0.65)',
          maxWidth: '24ch',
        }}
      >
        One for the whole home.
      </h2>
    </section>
  )
}

function Beat({ eyebrow, h2, body, isMobile, href, halfWidth, align = 'left' }) {
  // L-R-L-R alternation applies on both mobile AND desktop now.
  const alignRight = align === 'right'
  const scrim = isMobile
    ? {
        background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.7) 100%)',
        padding: '1.1rem 1.1rem 1.2rem',
        borderRadius: 12,
        backdropFilter: 'blur(4px)',
      }
    : {
        background: 'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.42) 100%)',
        padding: '1.3rem 1.6rem 1.5rem',
        borderRadius: 12,
        backdropFilter: 'blur(3px)',
        // Use px (not ch — that resolves against inherited 16px font, far smaller than h2's font)
        // so headlines like "can't fix this polluted air." fit on one line.
        maxWidth: halfWidth ? '44vw' : '720px',
      }

  const inner = (
    <div style={scrim}>
      <div
        style={{
          fontSize: '0.74rem',
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.7)',
          marginBottom: '0.8rem',
        }}
      >
        {eyebrow}
      </div>
      <h2
        style={{
          margin: 0,
          fontSize: isMobile ? 'clamp(1.7rem, 8vw, 2.4rem)' : 'clamp(2rem, 5vw, 3.6rem)',
          fontWeight: 600,
          letterSpacing: '-0.035em',
          lineHeight: 1.05,
          color: '#fff',
          // Wide enough for the longest 2-line headline ("can't fix this polluted air." = 28 chars)
          maxWidth: '28ch',
        }}
      >
        {h2}
      </h2>
      <p
        style={{
          marginTop: '1rem',
          // Wider so the scrim grows enough for "Your body doesn't adapt."
          // and "can't fix this polluted air." to fit on single lines.
          maxWidth: '56ch',
          fontSize: isMobile ? '0.95rem' : 'clamp(0.98rem, 1.4vw, 1.12rem)',
          lineHeight: 1.55,
          color: 'rgba(255,255,255,0.92)',
        }}
      >
        {body}
      </p>
      {href && (
        <div
          style={{
            marginTop: '0.9rem',
            fontSize: '0.78rem',
            color: 'rgba(255,255,255,0.7)',
            letterSpacing: '0.04em',
          }}
        >
          Read more →
        </div>
      )}
    </div>
  )

  return (
    <section
      style={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: alignRight ? 'flex-end' : 'flex-start',
        padding: isMobile ? '0 5vw 14vh' : '0 7vw 12vh',
        boxSizing: 'border-box',
        pointerEvents: 'none',
      }}
    >
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            pointerEvents: 'auto',
            textDecoration: 'none',
            color: 'inherit',
            maxWidth: halfWidth ? '50vw' : 'fit-content',
          }}
        >
          {inner}
        </a>
      ) : (
        inner
      )}
    </section>
  )
}
